/**
 * Utility functions for handling expression conversion between names and IDs
 * @author Muskan Thakur
 */

const CHIP_PATTERN_REGEX = /\[([^\]]+)\]/g;

/**
 * Helper function to escape regex special characters
 */
const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Helper function to find chip by ID or name
 */
const findChip = (identifier, customWorkflowVariables) => {
  if (!customWorkflowVariables) return null;

  const chipIdNum = parseInt(identifier);
  if (!isNaN(chipIdNum)) {
    const chip = customWorkflowVariables.find((v) => v.id === chipIdNum || v.id === identifier || String(v.id) === identifier);
    if (chip) return chip;
  }

  return customWorkflowVariables.find((v) => v.name === identifier) || null;
};

/**
 * Converts expression with chip names to expression with chip IDs
 */
export const convertExpressionNamesToIds = (expression, customWorkflowVariables) => {
  if (!expression || !customWorkflowVariables) return expression;

  let convertedExpression = expression;
  const regex = new RegExp(CHIP_PATTERN_REGEX);
  let match;

  while ((match = regex.exec(expression)) !== null) {
    const chipName = match[1];
    const chip = customWorkflowVariables.find((v) => v.name === chipName);

    if (chip && chip.id) {
      const escapedName = escapeRegex(match[0]);
      convertedExpression = convertedExpression.replace(new RegExp(escapedName, "g"), `[${chip.id}]`);
    }
  }

  return convertedExpression;
};

/**
 * Converts expression with chip IDs to expression with chip names
 * Replaces deleted chips (chips not found in customWorkflowVariables) with 0
 * Handles both chip IDs and chip names in the expression
 * Preserves original spacing in the expression
 */
export const convertExpressionIdsToNames = (expression, customWorkflowVariables) => {
  if (!expression || !customWorkflowVariables) return expression;

  let convertedExpression = expression;
  const regex = new RegExp(CHIP_PATTERN_REGEX);
  let match;
  const matches = [];

  while ((match = regex.exec(expression)) !== null) {
    matches.push({
      fullMatch: match[0],
      chipIdentifier: match[1],
    });
  }

  for (let i = matches.length - 1; i >= 0; i--) {
    const { fullMatch, chipIdentifier } = matches[i];
    const chip = findChip(chipIdentifier, customWorkflowVariables);
    const escapedFullMatch = escapeRegex(fullMatch);

    if (chip && chip.name) {
      convertedExpression = convertedExpression.replace(new RegExp(escapedFullMatch, "g"), `[${chip.name}]`);
    } else {
      convertedExpression = convertedExpression.replace(new RegExp(escapedFullMatch, "g"), "0");
    }
  }

  return convertedExpression;
};
